import classnames from 'classnames';
import equal from 'fast-deep-equal/react';
import get from 'lodash/get';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import Select from '~/Components/Select/Select';
import { RANK_BATTLE_TYPE_NAMES } from '~/constants';
import { State } from '~/Reducers';

import styles from './SeasonPicker.scss';

interface SeasonItem {
  title: string;
  value?: string;
  seasonNumber?: number;
}

export interface SeasonPickerState {
  account?: Account;
  seasonNumber?: string;
  battleType: StatBattleType;
  battleTypeRaw: BattleTypeName;
  division: DivisionName;
  seasonsTitlesMap: SeasonsTitlesMap;
}

const stateSelector = (state: State): SeasonPickerState => {
  return {
    account: state.ReducerApp.account,
    seasonNumber: state.ReducerApp.seasonNumber,
    battleType: state.ReducerApp.battleType,
    battleTypeRaw: state.ReducerApp.battleTypeRaw,
    division: state.ReducerApp.division,
    seasonsTitlesMap: state.ReducerApp.seasonsTitlesMap,
  };
};

const SeasonPicker = () => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const { t } = useTranslation();
  const history = useHistory();
  const params = useParams<LocationParams>();
  const state = useSelector<State, SeasonPickerState>(stateSelector, equal);
  const { account, seasonNumber, battleType, battleTypeRaw, seasonsTitlesMap } = state;

  const allSeasons = get(account, 'statistics.seasons', {});
  const seasons: any = {};

  for (const key in allSeasons) {
    const season = allSeasons[key];

    if (
      (RANK_BATTLE_TYPE_NAMES.includes(battleTypeRaw) &&
        Object.values(season).some((item) => item.hasOwnProperty(battleType))) ||
      ('rating' === battleTypeRaw && Object.keys(season).includes(battleType))
    ) {
      seasons[key] = season;
    }
  }

  const seasonItems: Array<SeasonItem> = [];

  seasonItems.push({
    title: t('Все сезоны'),
    value: 'all',
  });

  for (const key in seasons) {
    if (Object.keys(seasonsTitlesMap).includes(key)) {
      seasonItems.push({
        title: seasonsTitlesMap[key],
        value: key,
      });
    }
  }

  let selectedSeasonIndex = 0;
  seasonItems.forEach((seasonItem, index) => {
    if (seasonItem.value === seasonNumber) {
      selectedSeasonIndex = index;
    }
  });

  if (selectedSeasonIndex !== selectedIndex) {
    setSelectedIndex(selectedSeasonIndex);
  }

  const renderValue = () => {
    const itemData = seasonItems[selectedIndex];
    return (
      <div className={styles.value}>
        <div>{itemData.title}</div>
      </div>
    );
  };

  const renderItem = (index: number, isSelected: boolean, isUpper: boolean, isLower: boolean) => {
    const renderItemStyles = classnames(styles.item, {
      [styles.selected]: isSelected,
      [styles.upper]: isUpper,
      [styles.lower]: isLower,
    });
    const itemData = seasonItems[index];
    return (
      <div className={renderItemStyles}>
        <div>{itemData.title}</div>
      </div>
    );
  };

  const onSelectHandler = (index: number) => {
    setSelectedIndex(index);
    const value = seasonItems[index] ? seasonItems[index].value : undefined;
    if (value && params.id && params.battletype && seasonItems[index].value) {
      const pathToBattletype = `/statistics/${params.id}/${params.battletype}`;
      if (value === '0') {
        history.push(pathToBattletype);
      } else {
        history.push(pathToBattletype + `/${seasonItems[index].value}`);
      }
    }
  };

  useEffect(() => {
    if (seasonItems[0]) {
      onSelectHandler(0);
    }
  }, [battleType]);

  if (seasonItems.length === 0) {
    return null;
  }

  return (
    <Select
      maxItemsCount={5}
      items={seasonItems.map((item) => item.value)}
      selectedIndex={selectedSeasonIndex}
      onSelect={onSelectHandler}
      renderValue={renderValue}
      renderItem={renderItem}
    />
  );
};

export default SeasonPicker;
